<template>
  <div @click="onPlay">
    <div ref="containerWebGL" class="u-wrapper-panel" />
    <template v-if="!isLoading && sequence">
      <player @onNext="onNext" :slides="sequence.lorealSlides" :currentIndex="index" />
      <slider @onNext="onNext" :position="{x: 1, y: 1.5, z: 0}" v-if="isEnvLoaded" :slides="sequence.lorealSlides" :currentIndex="index" />
      <animated-avatar :character="sequence.lorealPersonnage" @objectReady="onObjectReady" />
    </template>
    <interface :isPlaying="isPlaying" @onToggle="onToggle" />
    <transition :duration="2000" name="loader">
      <loader v-if="isLoading || !isReady" />
    </transition>
  </div>
</template>

<script>
import Player from '@/components/player/Player'
import Slider from '@/components/player/Slider'

import AnimatedAvatar from '@/components/3d/AnimatedAvatar'
import Loader from '@/components/Loader'
import Interface from '@/components/Interface'

import envData from '@/assets/data/environments'

import { webGL } from '@/webGL/WebGL'
import { BaseEnvironment } from '@/webGL/objects/Environments/BaseEnvironment'
import { Trust } from '@/webGL/objects/Environments/Trust'
import { Science } from '@/webGL/objects/Environments/Science'
import { Health } from '@/webGL/objects/Environments/Health'

// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import {
  PlaneBufferGeometry,
  ShadowMaterial,
  MeshBasicMaterial,
  Mesh,
  BoxBufferGeometry,
  Vector3,
  Box3
} from 'three'


import { FitTo } from '@/utils/FitTo'
import { CameraMovement } from '@/webGL/CameraMovement'

export default {
  components: {
    Player,
    AnimatedAvatar,
    Slider,
    Loader,
    Interface
  },

  computed: {
    loaded () {
      return this.$store.getters['data/loaded']
    },
    sequences () {
      return this.$store.getters['data/sequences']
    },
    sequence () {
      return this.$store.getters['data/sequenceBySlug'](this.$route.params.sequence)
    },
    isLoading () {
      return !this.$store.getters['data/loaded'] || !this.sequence || !this.isEnvLoaded
    }
  },

  data () {
    return {
      index: -1,
      isEnvLoaded: false,
      isPlaying: false,
      isReady: false
    }
  },

  mounted () {
    this.getData()

    this.fitTo = new FitTo(webGL.camera)
    this.cameraMovement = new CameraMovement(webGL.sceneScreen, webGL.scenePerso, webGL.camera)
    // this.controls = new OrbitControls( webGL.camera, this.$el )

    const geo = new PlaneBufferGeometry(2000, 1000, 1, 1)
    const mat = new ShadowMaterial()
    mat.opacity = .3
    const mesh = new Mesh(geo, mat)
    mesh.rotation.x = -Math.PI * .5
    mesh.position.y = .5
    mesh.receiveShadow = true

    webGL.scenePerso.add(mesh)
  },

  methods: {
    getData () {
      this.$store.dispatch('data/presentation', this.$route.params.presentation).then(() => {
        if (!this.$route.params.sequence) {
          this.$router.push({
            name: 'Sequence',
            params: {
              presentation: this.$route.params.presentation,
              sequence: this.$store.getters['data/sequences'][0].slug
            }
          })
        }
        else {
          this.onInit()
        }
      })
    },
    onInit () {
      this.nextIndex = 0

      // this.baseSetup()
      this.isReady = true

      if (this.environment) {
        this.isEnvLoaded = false
        webGL.scene.remove(this.environment)
        this.environment.onDestroy()
      }
      if (this.avatar) {
        webGL.scenePerso.remove(this.avatar)
      }

      this.addEnvironment()
    },
    onNext () {
      this.nextIndex++
      if (this.nextIndex >= 2) {
        this.nextIndex = 0

        if (this.index + 1 === this.sequence.lorealSlides.length) {
          this.onNextSequence()
        }
        else {
          this.index++
          requestAnimationFrame(() => {
            this.$hub.emit('player:play')
          })
        }
      }
    },
    onPrev () {
      this.nextIndex = 0

      if (this.index === 0) {
        this.onPrevSequence()
      }
      else {
        this.index--
        requestAnimationFrame(() => {
          this.$hub.emit('player:play')
        })
      }
    },
    onNextSequence () {
      this.gotoSequence(this.$store.getters['data/sequences'][this.sequence.index + 1])
    },
    onPrevSequence () {
      this.gotoSequence(this.$store.getters['data/sequences'][this.sequence.index - 1])
    },
    gotoSequence (sequence) {
      if (sequence) {
        this.isReady = false

        this.$hub.emit('player:pause')

        setTimeout(() => {
          this.index = -1

          this.$router.push({
            name: 'Sequence',
            params: {
              presentation: this.$route.params.presentation,
              sequence: sequence.slug
            }
          })
        }, 2000)
      }
    },
    onToggle () {
      if (this.isPlaying) {
        this.onPause()
      }
      else {
        this.onPlay()
      }
    },
    onPlay () {
      this.index = this.index === -1 ? 0 : this.index
      this.isPlaying = true

      requestAnimationFrame(() => {
        this.$hub.emit('player:play')
      })
    },
    onPause () {
      this.$hub.emit('player:pause')
      this.isPlaying = false
    },
    onObjectReady (obj) {
      this.avatar = obj

      webGL.scenePerso.add(this.avatar)

      setTimeout(() => {
        this.cameraMovement.computeObject()
      }, 3000)
    },
    addEnvironment () {
      if (this.sequence.lorealEnvironent === 'trust') {
        this.environment = new Trust(envData[this.sequence.lorealEnvironent].url)
      }
      else if (this.sequence.lorealEnvironent === 'science') {
        this.environment = new Science(envData[this.sequence.lorealEnvironent].url)
      }
      else if (this.sequence.lorealEnvironent === 'health') {
        this.environment = new Health(envData[this.sequence.lorealEnvironent].url)
      }
      else {
        this.environment = new BaseEnvironment(envData[this.sequence.lorealEnvironent].url)
      }

      webGL.renderer.color = envData[this.sequence.lorealEnvironent].color

      this.environment.promise.then(() => {
        this.isEnvLoaded = true
        if (this.isPlaying) {
          setTimeout(this.onPlay, 2000)
        }
      })
      webGL.scene.add(this.environment)
    },
    onKeydown (event) {
      if (event === 39) { // right arrow
        this.nextIndex = 2
        this.onNext()
      }
      else if (event === 37) { // left arrow
        this.onPrev()
      }
      else if (event === 38) { // up arrow
        this.onNextSequence()
      }
      else if (event === 40) { // down arrow
        this.onPrevSequence()
      }
    },
    onResize () {
      this.cameraMovement.onResize()
    }
  },
  watch: {
    $route (to, from) {
      this.onInit()
    }
  }
}
</script>